import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Carousel from '../../atoms/carousel';
import ProductCard from '../../tissues/product-card';
import Typography from '../../atoms/typography';
import trackEvent from '../../utils/tracking';

import { useSiteSpect } from '../../utils/context/sitespect-provider';

const CarouselWrapper = styled.section`
  min-height: 10rem;
  position: relative;
  text-align: center;

  & + & {
    margin-top: 10rem;
  }
`;

const CarouselTitle = styled(Typography)`
  color: ${(props) => props.theme.color.brand};
`;

const ProductCardCarousel = (props) => {
  const { algolia = false } = useSiteSpect();

  const carouselSettings = {
    prevNextButtons: algolia ? props.products.length > 5 : props.products?.results?.length > 5,
    pageDots: false,
    groupCells: true,
    cellAlign: 'left',
    wrapAround: props.isInfinite && props.products?.results
      ? props.products.results.length > 5
      : props.isInfinite
  };

  const onCarouselRef = useCallback((node) => {
    if (node !== null) {
      node.on('change', () => {
        if (props.trackFor) {
          trackEvent({
            eventCategory: `PDP Recommendations - ${props.trackFor}`,
            eventAction: 'Scrolled'
          });
        }
      });
    }
  }, []);

  useEffect(() => {
    if (props.trackFor) {
      trackEvent({
        eventCategory: `PDP Recommendations - ${props.trackFor}`,
        eventAction: 'Shown',
        eventNonInteraction: true
      });
    }
  }, []);

  return (!algolia && props.products?.results && props.products?.results.length > 0)
  || (algolia && props.products?.length > 0)
    ? (
      <CarouselWrapper>
        <CarouselTitle element="h1" like="heading-3">{props.title}</CarouselTitle>
        <Carousel
          type="product-card"
          options={carouselSettings}
          disableImagesLoaded
          flickityRef={onCarouselRef}
          reloadOnUpdate
        >
          {
            !algolia ? props.products.results.map(((product, i) => (
              <ProductCard
                key={`${props.id}-${product.sku || product.title}`}
                product={product}
                showQuickShop={false}
                showWishlist={false}
                trackFor={props.trackFor}
                module={props.id}
                index={i + 1}
              />
            )))
              : props.products.map(((product, i) => (
                <ProductCard
                  taxonProducts={props.taxonProducts ?? true}
                  key={`${props.id}-${product?.variants?.[0]?.maisonette_sku ?? product.title}`}
                  product={product}
                  showQuickShop={false}
                  showWishlist={false}
                  trackFor={props.trackFor}
                  module={props.id}
                  index={i + 1}
                />
              )))
          }
        </Carousel>
      </CarouselWrapper>
    ) : null;
};

ProductCardCarousel.defaultProps = {
  isInfinite: false,
  trackFor: '',
  taxonProducts: true
};

ProductCardCarousel.propTypes = {
  products: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]).isRequired,
  title: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  isInfinite: PropTypes.bool,
  trackFor: PropTypes.string,
  taxonProducts: PropTypes.bool
};

export default ProductCardCarousel;
